import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './AdminDashboard.module.css';
import logoImage from '../../assets/logo.jpg';
import {
  getAdminStats,
  getAdminDoctors,
  getAdminUsers,
  getAdminBookings,
  getAdminBills,
  getAdminRecentBills,
} from '../../api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalData, setModalData] = useState([]);
  const [modalType, setModalType] = useState('');
  const [recentBills, setRecentBills] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const data = await getAdminStats();
        setStats(data);
        setError(null);
      } catch (err) {
        setError('Failed to load stats');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
    // Fetch recent bills for billing history
    getAdminRecentBills().then(setRecentBills).catch(() => setRecentBills([]));
  }, []);

  // Route protection: redirect to login if not authenticated
  useEffect(() => {
    if (!localStorage.getItem('token') && !localStorage.getItem('loggedIn')) {
      navigate('/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    console.log('Admin logout initiated from AdminDashboard');
    
    // Clear all possible authentication-related localStorage items
    const itemsToRemove = [
      'token',
      'loggedIn',
      'userEmail',
      'userRole',
      'userPassword',
      'userData',
      'adminLoggedIn',
      'authToken',
      'sessionToken'
    ];

    itemsToRemove.forEach(item => {
      if (localStorage.getItem(item)) {
        localStorage.removeItem(item);
      }
    });

    // Clear sessionStorage as well
    sessionStorage.clear();

    // Force a page reload to clear any React state
    console.log('Redirecting to login page');
    window.location.href = '/login';
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setSidebarOpen(false); // Close sidebar on mobile after navigation
  };

  const handleView = async (type) => {
    setModalOpen(true);
    setModalType(type);
    setModalTitle('');
    setModalData([]);
    try {
      let data = [];
      let title = '';
      if (type === 'doctors') {
        data = await getAdminDoctors();
        title = 'All Doctors';
      } else if (type === 'users') {
        data = await getAdminUsers();
        title = 'All Users';
      } else if (type === 'bookingsToday') {
        const today = new Date().toISOString().slice(0, 10);
        data = await getAdminBookings({ date: today });
        title = 'Bookings Today';
      } else if (type === 'billsToday') {
        const today = new Date().toISOString().slice(0, 10);
        data = await getAdminBills({ date: today });
        title = 'Bills Today';
      } else if (type === 'bookingsMonth') {
        const month = new Date().toISOString().slice(0, 7);
        data = await getAdminBookings({ month });
        title = 'Bookings This Month';
      } else if (type === 'billsMonth') {
        const month = new Date().toISOString().slice(0, 7);
        data = await getAdminBills({ month });
        title = 'Bills This Month';
      }
      setModalTitle(title);
      setModalData(data);
    } catch (err) {
      setModalTitle('Error');
      setModalData([{ error: 'Failed to load data' }]);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalTitle('');
    setModalData([]);
    setModalType('');
  };

  return (
    <div className={styles.dashboardWrapper}>
      {/* Mobile Menu Toggle */}
      <button
        className={styles.mobileMenuToggle}
        onClick={toggleSidebar}
        aria-label="Toggle menu"
      >
        ☰
      </button>

      {/* Sidebar Overlay */}
      <div
        className={`${styles.sidebarOverlay} ${sidebarOpen ? styles.open : ''}`}
        onClick={closeSidebar}
      />

      {/* Sidebar */}
      <div className={`${styles.sidebar} ${sidebarOpen ? styles.open : ''}`}>
        <div className={styles.logoContainer}>
          {/* Placeholder for logo */}
          <img src={logoImage} alt="RUPSA Clinic Logo" className={styles.logo} />
        </div>
        <div className={styles.welcome}>Welcome Admin</div>
        <ul className={styles.menuListBullets}>
          <li className={styles.menuBulletItem} onClick={() => handleNavigation('/admin/create-doctor')}>Create Doctor</li>
          <li className={styles.menuBulletItem} onClick={() => handleNavigation('/admin/create-user')}>Patient Management</li>
          <li className={styles.menuBulletItem} onClick={() => handleNavigation('/admin/create-booking')}>Create Booking</li>
          <li className={styles.menuBulletItem} onClick={() => handleNavigation('/admin/create-bill')}>Create Billing</li>
          <li className={styles.menuBulletItem} onClick={() => handleNavigation('/admin/upload-lab-report')}>Upload Report</li>
          {/* <li className={styles.menuBulletItem} onClick={() => handleNavigation('/admin/settings')}>Settings</li> */}
          <li className={styles.menuBulletItem} onClick={handleLogout}>Logout</li>
        </ul>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <h1 className={styles.title}> RUPSA CLINIC Appointment Booking App</h1>
        {loading ? (
          <div>Loading...</div>
        ) : error ? (
          <div style={{ color: 'red' }}>{error}</div>
        ) : (
          <div className={styles.cardsGrid}>
            <div className={styles.card}>
              <div className={styles.cardTitle}>No. Of Doctors</div>
              <div className={styles.cardNumber}>{stats.doctorCount ?? ''}</div>
              <button className={styles.viewBtn} onClick={() => handleView('doctors')}>View</button>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>No. Of Users</div>
              <div className={styles.cardNumber}>{stats.userCount ?? ''}</div>
              <button className={styles.viewBtn} onClick={() => handleView('users')}>View</button>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>No. Of Bookings today</div>
              <div className={styles.cardNumber}>{stats.bookingsToday ?? ''}</div>
              <button className={styles.viewBtn} onClick={() => handleView('bookingsToday')}>View</button>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>No. Of Billing today</div>
              <div className={styles.cardNumber}>{stats.billsToday ?? ''}</div>
              <button className={styles.viewBtn} onClick={() => handleView('billsToday')}>View</button>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Booking this Month</div>
              <div className={styles.cardNumber}>{stats.bookingsMonth ?? ''}</div>
              <button className={styles.viewBtn} onClick={() => handleView('bookingsMonth')}>View</button>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Billing this Month</div>
              <div className={styles.cardNumber}>{stats.billsMonth ?? ''}</div>
              <button className={styles.viewBtn} onClick={() => handleView('billsMonth')}>View</button>
            </div>
          </div>
        )}
        <div className={styles.billingHistoryBox}>
          <div className={styles.billingHistoryTitle}>Billing History</div>
          {recentBills.length > 0 ? (
            <table className={styles.modalTable}>
              <thead>
                <tr>
                  {Object.keys(recentBills[0]).map((key) => (
                    <th key={key}>{key}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recentBills.map((bill, idx) => (
                  <tr key={idx}>
                    {Object.values(bill).map((val, i) => (
                      <td key={i}>{typeof val === 'object' ? JSON.stringify(val) : String(val)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div>No billing history found.</div>
          )}
        </div>
      </div>

      {/* Modal for details */}
      {modalOpen && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{modalTitle}</h2>
              <button onClick={closeModal} className={styles.closeBtn}>X</button>
            </div>
            <div className={styles.modalBody}>
              {modalType === 'users' && Array.isArray(modalData) && modalData.length > 0 ? (
                <table className={styles.modalTable}>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Email</th>
                      <th>Password</th>
                      <th>Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalData.map((user, idx) => (
                      <tr key={idx}>
                        <td>{user._id || user.id || ''}</td>
                        <td>{user.email || ''}</td>
                        <td>{user.password || ''}</td>
                        <td>{user.role || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : Array.isArray(modalData) && modalData.length > 0 ? (
                <table className={styles.modalTable}>
                  <thead>
                    <tr>
                      {Object.keys(modalData[0]).map((key) => (
                        <th key={key}>{key}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {modalData.map((item, idx) => (
                      <tr key={idx}>
                        {Object.values(item).map((val, i) => (
                          <td key={i}>{typeof val === 'object' ? JSON.stringify(val) : String(val)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div>No data found.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard; 